import { Fragment } from 'react';

import {
  Badge,
  Button,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { NavLink as RouterLink } from 'react-router-dom';

import { CellContext, ColumnDef, createColumnHelper, Row, Table } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  CalendarOutline,
  DEFAULT_DATE_FORMAT,
  IndividualRole,
  IndividualStatus,
  ClipboardCloseOutline,
  MoreCircleOutline,
} from '@aslanpay/portal-common-lib';

import {
  IndividualActions,
  IndividualBulkActions,
  IndividualRow,
  IndividualType,
} from 'app/components/UI/Table/Table.types';

import { Role, Status } from 'app/constants/individual';
import {
  AdminField,
  EmployeeField,
  IndividualActionsIds,
  individualActionsValues,
} from 'app/constants/table';

import { getSelectedCompanyPath } from 'app/utils/routes';

export function getIndividualColumns(
  type: IndividualType,
  actions: IndividualActions,
  showSelectColumn = true,
  showOptionsColumn = true
) {
  const columnHelper = createColumnHelper<IndividualRow>();

  const Field = type === 'admin' ? AdminField : EmployeeField;

  const fields = Object.keys(Field) as Array<keyof IndividualRow>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<IndividualRow, any>[] = fields.map((field) => {
    return columnHelper.accessor(field as keyof IndividualRow, {
      cell: (info) => {
        switch (field as keyof IndividualRow) {
          case 'companyName':
            return (
              <Link
                as={RouterLink}
                to={getSelectedCompanyPath(info.row.original.companyId)}
                color="brand.purple"
                textDecoration="underline"
                onClick={(event) => event.stopPropagation()}
              >
                {info.getValue() as string}
              </Link>
            );
          case 'email':
            return <Text color="brand.purple">{info.getValue() as string}</Text>;
          case 'role':
            return Role[info.getValue() as IndividualRole];

          case 'status':
            return (
              <Badge colorScheme={`badge.${Status[info.getValue() as IndividualStatus]?.color}`}>
                {Status[info.getValue() as IndividualStatus]?.value}
              </Badge>
            );
          default:
            return info.getValue();
        }
      },

      header: Field[field as keyof typeof Field],
      enableSorting: field !== 'companyName',
      meta: {
        getCellContext: (context: CellContext<IndividualRow, unknown>) => {
          return {
            onClick: () => actions.edit && actions.edit([context.row.index]),
          };
        },
      },
    });
  });

  if (showSelectColumn) {
    columns.unshift({
      id: 'select',
      enableSorting: false,
      header: ({ table }: { table: Table<IndividualRow> }) => (
        <Checkbox
          {...{
            isChecked: table.getIsAllPageRowsSelected(),
            isIndeterminate: table.getIsSomePageRowsSelected(),
            onChange: table.getToggleAllPageRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }: { row: Row<IndividualRow> }) => (
        <Checkbox
          {...{
            isChecked: row.getIsSelected(),
            isIndeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      ),
      meta: {
        customStyles: { justifyContent: 'center' },
        pin: 'left',
        withBackground: true,
      },
    });
  }

  columns.push(
    columnHelper.display({
      id: 'icons',
      cell: (info) => {
        return (
          <HStack justify="end" spacing="8px">
            {info.row.original.onUnpaidLeave && (
              <Tooltip
                hasArrow
                shouldWrapChildren
                arrowPadding={0}
                placement="bottom-end"
                label="This employee is on accrual pause."
              >
                <ClipboardCloseOutline boxSize="24px" color="brand.unpaid" />
              </Tooltip>
            )}
            {info.row.original.terminationDate && info.row.original.status !== 'LEFT' && (
              <Tooltip
                hasArrow
                shouldWrapChildren
                arrowPadding={0}
                placement="bottom-end"
                label={`This employee is leaving employment on ${format(
                  parseISO(info.row.original.terminationDate),
                  DEFAULT_DATE_FORMAT
                )}.`}
              >
                <CalendarOutline
                  boxSize="24px"
                  color="brand.leaving"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (actions.editTerminate) {
                      actions.editTerminate([info.row.index]);
                    }
                  }}
                />
              </Tooltip>
            )}
          </HStack>
        );
      },
    })
  );

  if (showOptionsColumn) {
    columns.push(
      columnHelper.display({
        id: 'options',
        meta: {
          getCellContext: (context: CellContext<IndividualRow, unknown>) => {
            if (context.column.id === 'options') return { style: { padding: 0, textAlign: 'end' } };
            return undefined;
          },
          customStyles: { justifyContent: 'center' },
          pin: 'right',
        },
        cell: (info) => (
          <Menu autoSelect={false} strategy="fixed">
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<MoreCircleOutline boxSize="20px" />}
              variant="menu"
            />
            <Portal>
              <MenuList>
                {Object.keys(actions).flatMap((action, index) => {
                  const onActionClick = actions[action as keyof typeof actions];

                  if (onActionClick) {
                    const actionValue = individualActionsValues.find(
                      (value) => value.id === action
                    );

                    let isDisabled = false;

                    if (
                      actionValue?.id === IndividualActionsIds.INVITE &&
                      info.row.original.status !== 'CREATED' &&
                      info.row.original.status !== 'INVITED'
                    ) {
                      isDisabled = true;
                    } else if (
                      actionValue?.id === IndividualActionsIds.PAUSE &&
                      info.row.original.status !== 'ACTIVE'
                    ) {
                      isDisabled = true;
                    } else if (
                      actionValue?.id === IndividualActionsIds.REACTIVATE &&
                      info.row.original.status !== 'PAUSED'
                    ) {
                      return [];
                    } else if (actionValue?.id === IndividualActionsIds.SET_UNPAID) {
                      if (info.row.original.onUnpaidLeave) return [];

                      if (
                        !['CREATED', 'INVITED', 'ACTIVE', 'RESTRICTED'].includes(
                          info.row.original.status
                        )
                      ) {
                        isDisabled = true;
                      }
                    } else if (actionValue?.id === IndividualActionsIds.REMOVE_UNPAID) {
                      if (!info.row.original.onUnpaidLeave) return [];
                      if (info.row.original.status === 'LEFT') isDisabled = true;
                    } else if (
                      actionValue?.id === IndividualActionsIds.TERMINATE &&
                      (info.row.original.status === 'RESTRICTED' ||
                        info.row.original.status === 'LEFT')
                    ) {
                      return [];
                    } else if (
                      actionValue?.id === IndividualActionsIds.EDIT_TERMINATE &&
                      info.row.original.status !== 'RESTRICTED'
                    ) {
                      return [];
                    } else if (
                      actionValue?.id === IndividualActionsIds.REWARD_POINTS &&
                      (info.row.original.status === 'LEFT' ||
                        info.row.original.status === 'DELETED' ||
                        info.row.original.onUnpaidLeave)
                    ) {
                      return [];
                    }

                    return (
                      <Fragment key={action}>
                        {index > 0 && <MenuDivider key={`${info.row.index}${action}cenas`} />}
                        <MenuItem
                          key={action}
                          color={
                            action === IndividualActionsIds.DELETE ? 'system.error' : 'brand.black'
                          }
                          onClick={() => onActionClick([info.row.index])}
                          icon={
                            <Icon
                              as={actionValue?.icon}
                              boxSize="14px"
                              color={
                                action === IndividualActionsIds.DELETE
                                  ? 'system.error'
                                  : 'brand.darkGray'
                              }
                            />
                          }
                          isDisabled={isDisabled}
                        >
                          {actionValue?.value}
                        </MenuItem>
                      </Fragment>
                    );
                  }
                  return [];
                })}
              </MenuList>
            </Portal>
          </Menu>
        ),
      })
    );
  }

  return columns;
}

export function getIndividualBulkActionsValues<TData extends object>(
  label: IndividualType,
  actions: IndividualBulkActions,
  selectedRows: Row<TData>[]
) {
  const isInviteDisabled = selectedRows.some(
    (row) => !['CREATED', 'INVITED'].includes((row.original as IndividualRow).status)
  );

  const bulkActions = {
    ...actions,
    invite: { ...(actions as IndividualBulkActions).invite, isDisabled: isInviteDisabled },
  };

  const bulkActionsElements = Object.keys(bulkActions).map((bulkAction) => {
    const actionValue = individualActionsValues.find((value) => value.id === bulkAction);

    const variant = bulkAction === IndividualActionsIds.DELETE ? 'bulkDelete' : 'bulk';

    return (
      <Button
        key={bulkAction}
        size="bulk"
        variant={variant}
        onClick={() =>
          bulkActions[bulkAction as keyof IndividualBulkActions].action(
            selectedRows.map((row) => row.index)
          )
        }
        isDisabled={bulkActions[bulkAction as keyof IndividualBulkActions].isDisabled}
      >
        <Icon as={actionValue?.icon} boxSize="20px" />
        <Text>{actionValue?.value}</Text>
      </Button>
    );
  });

  return { label, bulkActions: bulkActionsElements };
}
